/**
 * How an invoice's Bill To block is composed from a customer and a contact.
 *
 * Shared so the editor can fill the block the moment a contact is picked,
 * with the same lines the server would build — rather than showing a stale
 * address until the next save.
 */

/** The name printed as the first Bill To line. Preferred name wins over the
 *  legal first name — it is what the person is called on correspondence. */
export function billToContactName(
  contact: {
    preferredName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null,
): string | null {
  if (!contact) return null;
  const name = [contact.preferredName ?? contact.firstName, contact.lastName]
    .filter(Boolean)
    .join(' ');
  return name.length > 0 ? name : null;
}

export interface BillToAddressSource {
  address1?: string | null;
  address2?: string | null;
  locality?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/**
 * One printable address, whatever shape it arrived in.
 *
 * The two sources of a Bill To address do NOT agree on field names, and that
 * disagreement is not cosmetic — it silently truncated addresses on issued
 * invoices until this normalization existed:
 *
 *   - `customers.billing_address` is written `{ street, city, state,
 *     postalCode, country }` — see the customer form's `AddressBlock` and the
 *     importer's `billingAddressStreet` / `billingAddressCity` columns.
 *   - `customer_contacts` holds `{ address1, address2, locality, region,
 *     postalCode, country }` as real columns.
 *
 * Reading a customer's billing address with the CONTACT's field names matched
 * only `postalCode` and `country`, which was enough to look like an address
 * and shadow the contact's complete one — so a customer with a billing address
 * printed as `Ana Reyes / 75024 / US`, with the street and city dropped.
 *
 * Both spellings are accepted for either source. The stored jsonb is untyped
 * at the DB boundary, so being liberal about what a row might hold costs
 * nothing and means a hand-written or migrated row cannot reintroduce the
 * truncation.
 */
interface NormalizedAddress {
  line1: string | null;
  line2: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
}

/**
 * Read an address of either shape, or `null` when the value carries nothing
 * printable.
 *
 * Null-when-empty is what preserves the fallback: `{}`, or a row of blank
 * strings, must not shadow the contact's address with nothing.
 */
function normalizeAddress(value: unknown): NormalizedAddress | null {
  if (typeof value !== 'object' || value === null) return null;
  const o = value as Record<string, unknown>;
  const str = (key: string): string | null => {
    const v = o[key];
    return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
  };

  const address: NormalizedAddress = {
    line1: str('street') ?? str('address1'),
    line2: str('address2'),
    locality: str('city') ?? str('locality'),
    region: str('state') ?? str('region'),
    postalCode: str('postalCode'),
    country: str('country'),
  };

  return Object.values(address).some((v) => v !== null) ? address : null;
}

/**
 * Compose the printed Bill To block: contact name, then the customer's billing
 * address, falling back to the contact's own.
 *
 * Mirrors the reference invoice, which reads company / contact name / street /
 * city-state-zip / country. Blank segments are dropped rather than printed as
 * empty lines, which would read to a customer as a data bug.
 *
 * Capped at the 6 lines the renderer's Bill To panel allows, so the invoice
 * prints the most identifying lines rather than an arbitrary tail.
 */
export function buildBillToAddressLines(
  contactName: string | null,
  billingAddress: unknown,
  contact: BillToAddressSource | null,
): string[] {
  const lines: string[] = [];
  if (contactName) lines.push(contactName);

  const source = normalizeAddress(billingAddress) ?? normalizeAddress(contact);
  if (!source) return lines;

  if (source.line1) lines.push(source.line1);
  if (source.line2) lines.push(source.line2);
  const cityLine = [source.locality, source.region, source.postalCode]
    .filter((s): s is string => s !== null)
    .join(', ');
  if (cityLine) lines.push(cityLine);
  if (source.country) lines.push(source.country);

  return lines.slice(0, 6);
}
